import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from "react";
import type {
  FocusEvent,
  HTMLInputTypeAttribute,
  MouseEvent,
  ReactElement,
} from "react";
import type { WrappedFieldInputProps } from "redux-form";
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import SelectBox from "../../../components/select-box";
import type {
  SelectBoxOwnProps,
  SelectBoxValue,
} from "../../../components/select-box";
import UpFieldWithErrorState from '../../../components/UpFieldWithErrorState';
import UpInput from '../../../components/up-input';
import UpInputWithLabel from '../../../components/UpInputWithLabel';
import globalMessages from '../../../messages/globalMessages';

import { PasswordRequirements } from '../../CustomerLogin/Components/password-requirements';

type LegacyInputText = string | ReactElement | Array<string | ReactElement>;

type InputField = Pick<
  WrappedFieldInputProps,
  "name" | "onBlur" | "onChange" | "onFocus"
> & {
  value: string;
};

interface InputMeta {
  error?: keyof typeof globalMessages;
  touched?: boolean;
}

interface InputBaseProps {
  autoComplete?: boolean;
  handleChange?: () => void;
  infoText?: string | ReactElement;
  input: InputField;
  label: string | ReactElement;
  margin?: string;
  meta: InputMeta;
  placeholder?: LegacyInputText;
  type: HTMLInputTypeAttribute;
  visible?: boolean;
  width?: string;
  withRequirements?: boolean;
}

type InputProps = InputBaseProps &
  (
    | {
        masked: true;
        toggleVisibility: (blur: boolean) => void;
      }
    | {
        masked?: false;
        toggleVisibility?: never;
      }
  );

interface InputState {
  active: boolean;
  requirements: boolean;
}

type SelectBoxFieldProps<Value extends SelectBoxValue> = Pick<
  SelectBoxOwnProps,
  "emptyMessage" | "margin" | "options" | "padding" | "placeholder"
> & {
  infoText?: string | ReactElement;
  input: {
    name: string;
    onChange: (value: Value) => void;
    value?: Value;
  };
  label: string | ReactElement;
};

const Button = styled.button`
  position: absolute;
  bottom: 7px;
  right: 8px;
  padding: 0;
`;

class Input extends React.Component<InputProps, InputState> {
  static propTypes = {
    input: PropTypes.object,
    visible: PropTypes.bool,
    width: PropTypes.string,
    margin: PropTypes.string,
    placeholder: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
      PropTypes.array,
    ]),
    type: PropTypes.string.isRequired,
    meta: PropTypes.object.isRequired,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    infoText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    autoComplete: PropTypes.bool,
    toggleVisibility: PropTypes.func,
    handleChange: PropTypes.func,
    masked: PropTypes.bool,
    withRequirements: PropTypes.bool,
  };

  constructor(props: InputProps) {
    super(props);
    this.state = {
      active: false,
      requirements: false,
    };
  }

  removeReadonlyAttr = (evt: FocusEvent<HTMLInputElement>) => {
    evt.currentTarget.removeAttribute('readonly');
  };

  handleFocus = () => {
    this.setState({
      active: true,
    });
  };

  handleBlur = (evt: FocusEvent<HTMLElement>) => {
    if (evt.relatedTarget === null && this.state.active && this.props.masked) {
      this.setState({
        active: false,
      });
      this.props.toggleVisibility(true);
    }
  };

  handleClick = (evt: MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    if (this.props.masked) this.props.toggleVisibility(false);
    evt.stopPropagation();
  };

  toggleRequirements = (show = false) => {
    this.setState({
      requirements: show,
    });
  };

  render() {
    const {
      masked,
      withRequirements,
      type,
      visible = false,
      label,
      placeholder,
      width,
      margin,
      infoText,
      input,
      autoComplete = false,
      meta: { touched, error },
      handleChange,
    } = this.props;
    const isError = Boolean(touched && error !== undefined);
    return (
      <UpFieldWithErrorState
        error={isError}
        errorMessage={
          <FormattedMessage {...globalMessages[error || 'requiredError']} />
        }
        label={label}
        margin={margin || '0 0 20px 0'}
        width={width}
        infoText={infoText}
      >
        <UpInput
          {...input}
          readOnly={autoComplete}
          onChange={(evt) => {
            input.onChange(evt);
            if (handleChange) handleChange();
          }}
          onFocus={(evt) => {
            input.onFocus(evt);
            if (autoComplete) this.removeReadonlyAttr(evt);
            if (masked) this.handleFocus();
            if (withRequirements) this.toggleRequirements(true);
          }}
          onBlur={(evt) => {
            input.onBlur(evt);
            if (masked) this.handleBlur(evt);
            if (withRequirements) this.toggleRequirements();
          }}
          type={type}
          placeholder={placeholder === undefined ? undefined : String(placeholder)}
          width={width}
          className={touched && error ? "error" : undefined}
          autoComplete="off"
        />
        {masked && !isError && input.value && this.state.active && (
          <Button
            onClick={(evt) => this.handleClick(evt)}
            onBlur={(evt) => this.handleBlur(evt)}
          >
            <FontAwesomeIcon
              icon={visible ? 'eye-slash' : 'eye'}
              tabIndex={0}
            />
          </Button>
        )}
        {this.state.requirements && (
          <PasswordRequirements password={input.value} />
        )}
      </UpFieldWithErrorState>
    );
  }
}

export { Input };

export const selectBox = <Value extends SelectBoxValue>(
  props: SelectBoxFieldProps<Value>,
) => (
  <UpInputWithLabel label={props.label} infoText={props.infoText}>
    <SelectBox
      width="100%"
      onChange={(_name, value: Value) => {
        props.input.onChange(value);
      }}
      options={props.options}
      value={props.input.value}
      name={props.input.name}
      placeholder={props.placeholder}
      inputDisabled
      margin={props.margin}
      padding={props.padding}
      emptyMessage={props.emptyMessage}
    />
  </UpInputWithLabel>
);

selectBox.propTypes = {
  input: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.array.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  infoText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  margin: PropTypes.string,
  padding: PropTypes.string,
  emptyMessage: PropTypes.string,
};
